import moment from "moment";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import Box from "../Box";
import Button from "../Button";
import FormField from "../FormField";
import Typography from "../Typography";
import DatePicker from "../DatePicker";
import Autocomplete from "../Autocomplete";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import PhoneNumberInput from "../PhoneNumberInput";
import AddressService from "../../services/common/address.service";
import ProfileService, { IDataUpdateOrganizationProfile } from "../../services/identity/profile.service";

import { Gender, Mode } from "../../constants/enums";
import { ICodename, ICountries, IUserProfile } from "../../commons/interfaces";


interface IBasicProfileProps {
    idHref: string;
    userProfile: IUserProfile;

    hidenIdCardInfo?: boolean;
    hidenPassportInfo?: boolean;
    hidenUserProfileByOrganization: boolean;

    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onLoading: (value: boolean) => void;
    onChangeUserProfile: (dataChange: IUserProfile) => void;
}

const PersonalInfo = ({
    idHref,
    userProfile,

    hidenIdCardInfo,
    hidenPassportInfo,
    hidenUserProfileByOrganization,

    onError,
    onSuccess,
    onLoading,
    onChangeUserProfile,

}: IBasicProfileProps) => {
    const strings = useStrings();
    const [openBtnUpdate, setOpenBtnUpdate] = useState<boolean>(false);

    const [listCountry, setListCountry] = useState<ICodename[]>([]);
    const [dataUserProfile, setDataUserProfile] = useState<IUserProfile>({} as IUserProfile);
    const [errorUserProfile, setErrorUserProfile] = useState<{ [k in keyof IUserProfile]?: string }>({});

    const [errorUserProfileByOrganization, setErrorUserProfileByOrganization] = useState<{ [k in keyof IDataUpdateOrganizationProfile]?: string }>({});
    const [dataUserProfileByOrganization, setDataUserProfileByOrganization] = useState<IDataUpdateOrganizationProfile>({} as IDataUpdateOrganizationProfile);

    const genderList: ICodename[] = useMemo(() => [
        { code: Gender.Male, name: strings.Common.MALE },
        { code: Gender.Female, name: strings.Common.FEMALE },
        // { code: Gender.Other, name: strings.Common.OTHER },
    ], [strings]);

    useEffect(() => { getDataUserProfile() }, [userProfile]);

    useEffect(() => {
        if (userProfile?.organizationId) {
            (async () => {
                try {
                    const result: ICountries[] = await AddressService.getCountries({ type: 1 });
                    const newData: ICodename[] = result.map((item) => {
                        return {
                            code: item.code,
                            name: item.name,
                        };
                    });

                    setListCountry(newData);
                } catch (error) { }
            })()
        }
    }, [userProfile?.organizationId]);

    const getDataUserProfile = () => {
        onLoading(true);

        let newData = {
            ...userProfile,
            email: userProfile?.email || "",
            avatarUrl: userProfile?.avatarUrl,
            birthDate: userProfile?.birthDate,
            fullName: userProfile?.fullName || "",
            gender: Number(userProfile?.gender || 0),
            phoneNumber: userProfile?.phoneNumber || "",
        };

        setErrorUserProfile({});
        setDataUserProfile(newData);

        // Info User Profile by Organization
        if (!hidenUserProfileByOrganization && !Helpers.isNullOrEmpty(userProfile?.organizationId)) {
            getDataUserProfileByOrganizationId(userProfile?.organizationId);
        }

        onLoading(false);
    }

    const getDataUserProfileByOrganizationId = async (organizationId: string) => {
        try {
            const resultProfile = await ProfileService.getUserInfo();

            let itemOrganizationUserProfile = [...resultProfile.organizationUserProfiles || []]?.find((item) => (
                item.organizationId === organizationId
            ));

            setErrorUserProfileByOrganization({});

            setDataUserProfileByOrganization({
                id: itemOrganizationUserProfile?.id,
                userId: itemOrganizationUserProfile?.userId,
                userType: itemOrganizationUserProfile?.userType,
                organizationId: itemOrganizationUserProfile?.organizationId,

                lastName: itemOrganizationUserProfile?.lastName,
                firstName: itemOrganizationUserProfile?.firstName,
                dateOfBirth: itemOrganizationUserProfile?.dateOfBirth,
                gender: Number(itemOrganizationUserProfile?.gender || 0),
                email: itemOrganizationUserProfile?.email || userProfile?.email,
                phoneNumber: itemOrganizationUserProfile?.phoneNumber || userProfile?.phoneNumber,

                idCardNo: itemOrganizationUserProfile?.idCardNo,
                idCardIssuedDate: itemOrganizationUserProfile?.idCardIssuedDate,
                idCardIssuedPlace: itemOrganizationUserProfile?.idCardIssuedPlace,
                socialInsuranceCode: itemOrganizationUserProfile?.socialInsuranceCode,
                employmentDate: itemOrganizationUserProfile?.employmentDate,

                passportNo: itemOrganizationUserProfile?.passportNo,
                nationality: itemOrganizationUserProfile?.nationality,
                passportExpiredDate: itemOrganizationUserProfile?.passportExpiredDate,
                passportIssuedPlace: itemOrganizationUserProfile?.passportIssuedPlace,
            });

        } catch (error) {

        }
    };

    const handleValueDate = (value: any) => {
        return (Helpers.isNullOrEmpty(value) || value === "0" || value === 0) ? undefined : Number(value) * 1000;
    };

    const checkValidate = () => {
        let checked: boolean = true;
        const newError: any = { ...errorUserProfile };

        // Info User Profile by Organization
        const newErrorByOrganization: any = { ...errorUserProfileByOrganization };

        if (!hidenUserProfileByOrganization && !Helpers.isNullOrEmpty(userProfile?.organizationId)) {
            if (Helpers.isNullOrEmpty((dataUserProfileByOrganization as any)?.["firstName"])) {
                newErrorByOrganization["firstName"] = strings.Common.REQUIRED;
                checked = false;
            }
            if (Helpers.isNullOrEmpty((dataUserProfileByOrganization as any)?.["lastName"])) {
                newErrorByOrganization["lastName"] = strings.Common.REQUIRED;
                checked = false;
            }

            if (Helpers.isNullOrEmpty((dataUserProfileByOrganization as any)?.["phoneNumber"])) {
                newErrorByOrganization["phoneNumber"] = strings.Common.REQUIRED;
                checked = false;
            } else {
                if ((dataUserProfileByOrganization as any)?.["phoneNumber"]?.length < 8) {
                    newErrorByOrganization["phoneNumber"] = strings.Common.WRONG_PHONE_NUMBER;
                    checked = false;
                }
            }

            if (Helpers.isNullOrEmpty((dataUserProfileByOrganization as any)?.["email"])) {
                newErrorByOrganization["email"] = strings.Common.REQUIRED;
                checked = false;
            } else {
                if (!Helpers.isValidEmail((dataUserProfileByOrganization as any)?.["email"])) {
                    newErrorByOrganization["email"] = strings.Common.INVALID_EMAIL;
                    checked = false;
                }
            }

            if (!Helpers.isNullOrEmpty(handleValueDate((dataUserProfileByOrganization as any)?.["dateOfBirth"])) &&
                Number((dataUserProfileByOrganization as any)?.["dateOfBirth"]) >= moment().unix()) {
                newErrorByOrganization["dateOfBirth"] = strings.Common.INVALID_TIME;
                checked = false;
            }

            if (!hidenIdCardInfo) {
                if (!Helpers.isNullOrEmpty(handleValueDate(dataUserProfileByOrganization?.idCardIssuedDate)) &&
                    Number(dataUserProfileByOrganization?.idCardIssuedDate) >= moment().unix()) {
                    newErrorByOrganization["idCardIssuedDate"] = strings.Common.INVALID_TIME;
                    checked = false;
                }

                if (!Helpers.isNullOrEmpty(dataUserProfileByOrganization?.idCardNo) &&
                    dataUserProfileByOrganization?.idCardNo?.length < 12) {
                    newErrorByOrganization["idCardNo"] = strings.Staff.WRONG_ID_CARD_NO_FORMAT;
                    checked = false;
                }

                if (!Helpers.isNullOrEmpty(dataUserProfileByOrganization?.socialInsuranceCode) &&
                    dataUserProfileByOrganization?.socialInsuranceCode?.length < 10) {
                    newErrorByOrganization["socialInsuranceCode"] = strings.Staff.WRONG_SOCIAL_INSURANCE_CODE_FORMAT;
                    checked = false;
                }
            }
            if (!hidenPassportInfo) {
                if (!Helpers.isNullOrEmpty(dataUserProfileByOrganization?.passportNo) &&
                    dataUserProfileByOrganization?.passportNo?.length < 12) {
                    newErrorByOrganization["passportNo"] = strings.Staff.WRONG_PASSPORT_NO_CODE_FORMAT;
                    checked = false;
                }
            }
        } else {
            if (Helpers.isNullOrEmpty((dataUserProfile as any)?.["phoneNumber"])) {
                newError["phoneNumber"] = strings.Common.REQUIRED;
                checked = false;
            } else {
                if ((dataUserProfile as any)?.["phoneNumber"]?.length < 8) {
                    newError["phoneNumber"] = strings.Common.WRONG_PHONE_NUMBER;
                    checked = false;
                }
            }

            if (Helpers.isNullOrEmpty(userProfile?.organizationId)) {
                if (!Helpers.isNullOrEmpty(handleValueDate(dataUserProfile.birthDate)) &&
                    Number(dataUserProfile.birthDate) >= moment().unix()) {
                    newError["birthDate"] = strings.Common.INVALID_TIME;
                    checked = false;
                }
            }
        }

        if (!checked) {
            setErrorUserProfile(newError);

            setErrorUserProfileByOrganization(newErrorByOrganization);

            onError(strings.Common.PLEASE_COMPLETE_ALL_INFORMATION);
        }

        return checked;
    };

    const handleChangeProfile = async () => {
        if (!checkValidate()) {
            return;
        } else {
            try {
                onLoading(true);
                let fullName = dataUserProfile?.fullName;

                // Info User Profile by Organization
                if (!hidenUserProfileByOrganization && !Helpers.isNullOrEmpty(userProfile?.organizationId)) {
                    fullName = `${dataUserProfileByOrganization?.lastName || ""} ${dataUserProfileByOrganization?.firstName || ""}`.trim();

                    await ProfileService.updateOrganizationProfile([{
                        id: dataUserProfileByOrganization?.id,
                        userId: dataUserProfileByOrganization?.userId,
                        userType: dataUserProfileByOrganization?.userType,
                        organizationId: dataUserProfileByOrganization?.organizationId,

                        email: dataUserProfileByOrganization?.email,
                        gender: dataUserProfileByOrganization?.gender || 0,
                        lastName: dataUserProfileByOrganization?.lastName,
                        firstName: dataUserProfileByOrganization?.firstName,
                        phoneNumber: dataUserProfileByOrganization?.phoneNumber,
                        dateOfBirth: Number(dataUserProfileByOrganization?.dateOfBirth || 0),

                        idCardNo: dataUserProfileByOrganization?.idCardNo,
                        idCardIssuedPlace: dataUserProfileByOrganization?.idCardIssuedPlace,
                        idCardIssuedDate: Number(dataUserProfileByOrganization?.idCardIssuedDate || 0),
                        socialInsuranceCode: dataUserProfileByOrganization?.socialInsuranceCode,
                        employmentDate: Number(dataUserProfileByOrganization?.employmentDate || 0),

                        passportNo: dataUserProfileByOrganization?.passportNo,
                        nationality: dataUserProfileByOrganization?.nationality,
                        passportIssuedPlace: dataUserProfileByOrganization?.passportIssuedPlace,
                        passportExpiredDate: Number(dataUserProfileByOrganization?.passportExpiredDate || 0),
                    }]);


                } else {
                    await ProfileService.updateUserProfile({
                        fullName: dataUserProfile?.fullName,
                        phoneNumber: dataUserProfile?.phoneNumber,
                        gender: Number(dataUserProfile?.gender || 0),
                        birthDate: Number(dataUserProfile?.birthDate || 0),

                        email: userProfile?.email,
                        userName: userProfile?.userName,
                    });
                };

                let dataChange: IUserProfile = {
                    ...userProfile,

                    fullName: fullName,
                    phoneNumber: dataUserProfile?.phoneNumber,
                    gender: Number(dataUserProfile?.gender || 0),
                    birthDate: Number(dataUserProfile?.birthDate || 0),
                };

                setErrorUserProfile({});
                setOpenBtnUpdate(false);

                onChangeUserProfile(dataChange);
                onSuccess(strings.Profile.CHANGE_PROFILE_SUCCESS);
            } catch (error: any) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onLoading(false);
            }
        }
    };

    return (
        <Box key={`pi`} id={idHref} p={2}>
            <Typography variant="h5">{strings.Profile.PERSONAL_INFORMATION}</Typography>
            <Grid container spacing={3} p={2}>
                {Helpers.isNullOrEmpty(userProfile?.organizationId) &&
                    <>
                        {/* FULL_NAME */}
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                maxLength={255}
                                label={strings.Staff.FULL_NAME}
                                placeholder={strings.Staff.ENTER_NAME}
                                defaultValue={dataUserProfile?.fullName}
                                errorMessage={errorUserProfile?.fullName}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onBlur={(value: any) => {
                                    setDataUserProfile(prev => ({ ...prev, fullName: value }));
                                    setErrorUserProfile(prev => ({ ...prev, fullName: undefined }));
                                }}
                            />
                        </Grid>

                        {/* PHONE NUMBER */}
                        <Grid item xs={12} md={6}>
                            <PhoneNumberInput
                                required
                                country={"vn"}
                                defaultValue={dataUserProfile?.phoneNumber}
                                errorMessage={errorUserProfile?.phoneNumber}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onChangeValue={(value: any) => {
                                    setDataUserProfile(prev => ({ ...prev, phoneNumber: value }));
                                    setErrorUserProfile(prev => ({ ...prev, phoneNumber: undefined }));
                                }}
                            />
                        </Grid>

                        {/* EMAIL */}
                        <Grid item xs={12} md={6}>
                            <FormField
                                disabled
                                maxLength={255}
                                label={strings.Staff.EMAIL}
                                defaultValue={dataUserProfile?.email}
                                errorMessage={errorUserProfile?.email}
                                placeholder={strings.Staff.ENTER_EMAIL}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onBlur={(value: any) => {
                                    setDataUserProfile(prev => ({ ...prev, email: value }));
                                    setErrorUserProfile(prev => ({ ...prev, email: undefined }));
                                }}
                            />
                        </Grid>

                        {/* GENDER */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                data={genderList || []}
                                label={strings.Staff.GENDER}
                                placeholder={strings.Staff.SELECT_GENDER}
                                defaultValue={dataUserProfile?.gender}
                                errorMessage={errorUserProfile?.gender}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onChange={(value) => {
                                    const newValue = value ? Number(value) : undefined;
                                    setDataUserProfile(prev => ({ ...prev, gender: newValue }));
                                    setErrorUserProfile(prev => ({ ...prev, gender: undefined }));
                                }}
                            />
                        </Grid>

                        {/* BIRTHDAY */}
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label={strings.Staff.BIRTHDAY}
                                placeholder={strings.Staff.SELECT_BIRTHDAY}
                                errorMessage={errorUserProfile?.birthDate}
                                value={handleValueDate(dataUserProfile?.birthDate)}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onChangeValue={(value: any) => {
                                    const newValue = value ? moment(value).unix() : undefined;
                                    setDataUserProfile(prev => ({ ...prev, birthDate: newValue }));
                                    setErrorUserProfile(prev => ({ ...prev, birthDate: undefined }));
                                }}
                            />
                        </Grid>
                    </>
                }

                {/* Info User Profile by Organization */}
                {!hidenUserProfileByOrganization &&
                    !Helpers.isNullOrEmpty(userProfile?.organizationId) &&
                    <>
                        {/* LAST_NAME */}
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                maxLength={255}
                                label={strings.Staff.LAST_NAME}
                                placeholder={strings.Staff.ENTER_LAST_NAME}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                defaultValue={dataUserProfileByOrganization?.lastName}
                                errorMessage={errorUserProfileByOrganization?.lastName}
                                onBlur={(value: any) => {
                                    setDataUserProfileByOrganization(prev => ({ ...prev, lastName: value }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, lastName: undefined }));
                                }}
                            />
                        </Grid>
                        {/* FIRST_NAME */}
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                maxLength={255}
                                label={strings.Staff.FIRST_NAME}
                                placeholder={strings.Staff.ENTER_FIRST_NAME}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                defaultValue={dataUserProfileByOrganization?.firstName}
                                errorMessage={errorUserProfileByOrganization?.firstName}
                                onBlur={(value: any) => {
                                    setDataUserProfileByOrganization(prev => ({ ...prev, firstName: value }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, firstName: undefined }));
                                }}
                            />
                        </Grid>

                        {/* PHONE NUMBER */}
                        <Grid item xs={12} md={6}>
                            <PhoneNumberInput
                                required
                                country={"vn"}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                defaultValue={dataUserProfileByOrganization?.phoneNumber}
                                errorMessage={errorUserProfileByOrganization?.phoneNumber}
                                onChangeValue={(value: any) => {
                                    setDataUserProfileByOrganization(prev => ({ ...prev, phoneNumber: value }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, phoneNumber: undefined }));
                                }}
                            />
                        </Grid>

                        {/* EMAIL */}
                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                maxLength={255}
                                label={strings.Staff.EMAIL}
                                placeholder={strings.Staff.ENTER_EMAIL}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                defaultValue={dataUserProfileByOrganization?.email}
                                errorMessage={errorUserProfileByOrganization?.email}
                                onBlur={(value: any) => {
                                    setDataUserProfileByOrganization(prev => ({ ...prev, email: value }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, email: undefined }));
                                }}
                            />
                        </Grid>

                        {/* GENDER */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                data={genderList || []}
                                label={strings.Staff.GENDER}
                                placeholder={strings.Staff.SELECT_GENDER}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                defaultValue={dataUserProfileByOrganization?.gender}
                                errorMessage={errorUserProfileByOrganization?.gender}
                                onChange={(value) => {
                                    const newValue = value ? Number(value) : undefined;
                                    setDataUserProfileByOrganization(prev => ({ ...prev, gender: newValue }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, gender: undefined }));
                                }}
                            />
                        </Grid>

                        {/* BIRTHDAY */}
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label={strings.Staff.BIRTHDAY}
                                placeholder={strings.Staff.SELECT_BIRTHDAY}
                                errorMessage={errorUserProfileByOrganization?.dateOfBirth}
                                value={handleValueDate(dataUserProfileByOrganization?.dateOfBirth)}
                                mode={openBtnUpdate ? Mode.Update : Mode.View}
                                onChangeValue={(value: any) => {
                                    const newValue = value ? moment(value).unix() : undefined;
                                    setDataUserProfileByOrganization(prev => ({ ...prev, dateOfBirth: newValue }));
                                    setErrorUserProfileByOrganization(prev => ({ ...prev, dateOfBirth: undefined }));
                                }}
                            />
                        </Grid>

                        {!hidenIdCardInfo &&
                            <>
                                {/* ID_CARD_NO */}
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        maxLength={12}
                                        type={"number"}
                                        label={strings.Staff.ID_CARD_NO}
                                        placeholder={strings.Staff.ENTER_ID_CARD_NO}
                                        defaultValue={dataUserProfileByOrganization?.idCardNo}
                                        errorMessage={errorUserProfileByOrganization?.idCardNo}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        onBlur={(value: any) => {
                                            const newValue = value ? ((Number(value) > 0) ? Number(value) : 0).toString() : undefined;
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, idCardNo: undefined }));
                                            setDataUserProfileByOrganization(prev => ({ ...prev, idCardNo: newValue }));
                                        }}
                                    />
                                </Grid>
                                {/* ID_CARD_ISSUED_DATE */}
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label={strings.Staff.ID_CARD_ISSUED_DATE}
                                        placeholder={strings.Staff.ID_CARD_ISSUED_DATE}
                                        errorMessage={errorUserProfileByOrganization?.idCardIssuedDate}
                                        value={handleValueDate(dataUserProfileByOrganization?.idCardIssuedDate)}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        onChangeValue={(value: any) => {
                                            const newValue = value ? moment(value).unix() : undefined;
                                            setDataUserProfileByOrganization(prev => ({ ...prev, idCardIssuedDate: newValue }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, idCardIssuedDate: undefined }));
                                        }}
                                    />
                                </Grid>
                                {/* ID_CARD_ISSUED_PLACE */}
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        maxLength={255}
                                        label={strings.Staff.ID_CARD_ISSUED_PLACE}
                                        placeholder={strings.Staff.ENTER_ID_CARD_ISSUED_PLACE}
                                        defaultValue={dataUserProfileByOrganization?.idCardIssuedPlace}
                                        errorMessage={errorUserProfileByOrganization?.idCardIssuedPlace}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        onBlur={(value: any) => {
                                            setDataUserProfileByOrganization(prev => ({ ...prev, idCardIssuedPlace: value }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, idCardIssuedPlace: undefined }));
                                        }}
                                    />
                                </Grid>
                                {/* SOCIAL_INSURANCE_CODE */}
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        maxLength={10}
                                        type={"number"}
                                        label={strings.Staff.SOCIAL_INSURANCE_CODE}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        placeholder={strings.Staff.ENTER_SOCIAL_INSURANCE_CODE}
                                        defaultValue={dataUserProfileByOrganization?.socialInsuranceCode}
                                        errorMessage={errorUserProfileByOrganization?.socialInsuranceCode}
                                        onBlur={(value: any) => {
                                            const newValue = value ? ((Number(value) > 0) ? Number(value) : 0).toString() : undefined;
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, socialInsuranceCode: undefined }));
                                            setDataUserProfileByOrganization(prev => ({ ...prev, socialInsuranceCode: newValue }));
                                        }}
                                    />
                                </Grid>
                                {/* EMPLOYMENT DATE */}
                                {/* <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label={strings.Staff.EMPLOYMENT_DATE}
                                            placeholder={strings.Staff.SELECT_EMPLOYMENT_DATE}
                                            errorMessage={errorUserProfileByOrganization?.employmentDate}
                                            value={handleValueDate(dataUserProfileByOrganization?.employmentDate)}
                                            mode={openBtnUpdate ? Mode.Update : Mode.View}
                                            onChangeValue={(value: any) => {
                                                const newValue = value ? moment(value).unix() : undefined;
                                                setDataUserProfileByOrganization(prev => ({ ...prev, employmentDate: newValue }));
                                                setErrorUserProfileByOrganization(prev => ({ ...prev, employmentDate: undefined }));
                                            }}
                                        />
                                    </Grid> */}
                            </>
                        }

                        {!hidenPassportInfo &&
                            <>
                                {/* PASSPORT NUMBER */}
                                <Grid item xs={12} md={6}>
                                    <FormField
                                        maxLength={12}
                                        label={strings.Staff.PASSPORT_NUMBER}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        placeholder={strings.Staff.ENTER_PASSPORT_NUMBER}
                                        defaultValue={dataUserProfileByOrganization?.passportNo}
                                        errorMessage={errorUserProfileByOrganization?.passportNo}
                                        onBlur={(value: any) => {
                                            setDataUserProfileByOrganization(prev => ({ ...prev, passportNo: value }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, passportNo: undefined }));
                                        }}
                                    />
                                </Grid>

                                {/* PASSPORT EXPDATE */}
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        views={["month", "year"]}
                                        label={strings.Staff.PASSPORT_EXPDATE}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        placeholder={strings.Staff.ENTER_PASSPORT_EXPDATE}
                                        errorMessage={errorUserProfileByOrganization?.passportExpiredDate}
                                        value={handleValueDate(dataUserProfileByOrganization?.passportExpiredDate)}
                                        onChangeValue={(value: any) => {
                                            const newValue = value ? moment(value).unix() : undefined;
                                            setDataUserProfileByOrganization(prev => ({ ...prev, passportExpiredDate: newValue }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, passportExpiredDate: undefined }));
                                        }}
                                    />
                                </Grid>

                                {/* PASSPORT ISSUING COUNTRY */}
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        data={listCountry || []}
                                        label={strings.Staff.PASSPORT_ISSUING_COUNTRY}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        placeholder={strings.Staff.SELECT_PASSPORT_ISSUING_COUNTRY}
                                        defaultValue={dataUserProfileByOrganization?.passportIssuedPlace}
                                        errorMessage={errorUserProfileByOrganization?.passportIssuedPlace}
                                        onChange={(value) => {
                                            setDataUserProfileByOrganization(prev => ({ ...prev, passportIssuedPlace: value }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, passportIssuedPlace: undefined }));
                                        }}
                                    />
                                </Grid>

                                {/* NATIONALITY */}
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        data={listCountry || []}
                                        label={strings.Staff.NATIONALITY}
                                        placeholder={strings.Staff.SELECT_NATIONALITY}
                                        mode={openBtnUpdate ? Mode.Update : Mode.View}
                                        defaultValue={dataUserProfileByOrganization?.nationality}
                                        errorMessage={errorUserProfileByOrganization?.nationality}
                                        onChange={(value) => {
                                            setDataUserProfileByOrganization(prev => ({ ...prev, nationality: value }));
                                            setErrorUserProfileByOrganization(prev => ({ ...prev, nationality: undefined }));
                                        }}
                                    />
                                </Grid>
                            </>
                        }
                    </>
                }

                <Grid item xs={12}>
                    <Box display="flex" gap={1} style={{ float: "right" }}>
                        {openBtnUpdate && (
                            <Button
                                color="secondary"
                                onClick={() => {
                                    getDataUserProfile();
                                    setOpenBtnUpdate(false);
                                }}
                            >
                                {strings.Common.CANCEL}
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                if (openBtnUpdate) {
                                    handleChangeProfile();
                                } else {
                                    setOpenBtnUpdate(true);
                                }
                            }}
                        >
                            {openBtnUpdate ? strings.Common.UPDATE : strings.Common.EDIT}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PersonalInfo;
